/** Outline detection for one image: split/order preview, thresholds, re-analysis with progress. */

import { Loader2, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { api, fileUrl } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";
import { Row, Slider } from "./ui";

type Params = Record<string, number | string | null>;

const FIELDS: { key: string; label: string; min: number; max: number; step: number; hint: string; auto?: boolean }[] = [
  { key: "darkMax", label: "Darkness", min: 20, max: 180, step: 1, hint: "Pixels darker than this (max of R,G,B) can be outline" },
  { key: "neutralTol", label: "Neutral tolerance", min: 2, max: 80, step: 1, hint: "How grey a dark pixel must be; tinted darks (navy) count as fill below this" },
  { key: "lineDilate", label: "Line margin", min: 0, max: 4, step: 1, hint: "Pixels around lines drawn with the outline (anti-aliasing)" },
  { key: "spurLen", label: "Drop spurs under", min: 0, max: 40, step: 1, hint: "Skeleton branches shorter than this (px) are dropped" },
  { key: "bridgeGap", label: "Join gaps under", min: 0, max: 80, step: 1, hint: "Stroke ends closer than this are drawn without lifting the pen (0 = auto)", auto: true },
  { key: "hatchSpacing", label: "Colour stroke spacing", min: 0, max: 40, step: 1, hint: "Spacing of colour-fill strokes in px (0 = auto)", auto: true },
  { key: "hatchJitter", label: "Hand wobble", min: 0, max: 0.5, step: 0.01, hint: "Randomness of the colouring strokes" },
];

export function AnalysisDialog({ assetId }: { assetId: string }) {
  const project = useEditor((s) => s.project)!;
  const st = useEditor((s) => s.analysis[assetId]);
  const asset = project.assets.find((a) => a.id === assetId);
  const current = (asset?.analysis?.params ?? {}) as unknown as Params;
  const [params, setParams] = useState<Params>({ ...current });
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });
  if (!asset) return null;
  const job = st?.job;
  const running = !!job && (job.status === "queued" || job.status === "running");
  const changed = FIELDS.some((f) => params[f.key] !== current[f.key]);

  const run = async () => {
    setErr("");
    try {
      useEditor.getState().edit((d) => {
        const a = d.assets.find((x) => x.id === assetId)!;
        if (a.analysis) Object.assign(a.analysis.params, params);
      });
      await flush();
      await api.analyze(project.id, assetId, { ...current, ...params });
      window.dispatchEvent(new Event("studio:poll"));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal analysis" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>{asset.name}</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <div className="analysis-body">
          <div className="analysis-preview">
            {st?.ready ? (
              <img src={fileUrl(project.id, `derived/${assetId}/preview.png`, st.stamp)} alt="outline/fill split and stroke order" />
            ) : (
              <div className="muted">Analysing…</div>
            )}
            <p className="muted small">Left: outline (dark) vs. colour fill (blue). Right: drawing order, blue → red, with group numbers.</p>
          </div>
          <div className="analysis-side">
            {FIELDS.map((f) => (
              <Row key={f.key} label={f.label} hint={f.hint}>
                <Slider value={Number(params[f.key] ?? 0)} min={f.min} max={f.max} step={f.step} onChange={(v) => setParams({ ...params, [f.key]: v })} />
                <span className="val">{f.auto && !params[f.key] ? "auto" : params[f.key]}</span>
              </Row>
            ))}
            <div className="analysis-actions">
              <button onClick={() => setParams({ ...current })} disabled={!changed}><RotateCcw size={14} /> Reset</button>
              <button className="primary" onClick={run} disabled={running}>
                {running ? <><Loader2 size={14} className="spin" /> {job!.status === "queued" ? "Queued" : `${Math.round(job!.progress * 100)}%`}</> : "Re-analyse"}
              </button>
            </div>
            {err && <p className="bad">{err}</p>}
            {job?.status === "error" && <p className="bad">Analysis failed: {job.message}</p>}
            {st?.stats && (
              <dl className="stats">
                <dt>Outline strokes</dt><dd>{st.stats.outlineStrokes}</dd>
                <dt>Colour strokes</dt><dd>{st.stats.fillStrokes}</dd>
                <dt>Coverage</dt><dd>{(100 * st.stats.outlineCoverage).toFixed(2)}% / {(100 * st.stats.fillCoverage).toFixed(2)}%</dd>
                <dt>Analysis time</dt><dd>{st.stats.seconds} s</dd>
              </dl>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
